import { useState } from "react";
import RequiredFieldTooltip, {
  ToolTipProps,
} from "../global/RequiredFieldTooltip";
import { useAdminContext } from "../../services/context/TourPopupContext";
import { twMerge } from "tailwind-merge";
import useMobile from "../../utils/helpers/useMobile";
import CustomText from "../atomic/CustomText";
interface GradientInfo {
  startColor: string;
  endColor: string;
  hoverStart: string;
  hoverEnd: string;
  hoverMiddle?: string;
}
interface DashboardActionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  title: any;
  description?: string;
  gradientInfo?: GradientInfo;
  Icon: any;
  toolTipInfo?: ToolTipProps;
}

const ActionButton = ({
  Icon,
  title,
  description,
  iconContainerClassName,
  ...props
}: any) => {
  if (props.isMobile) {
    return (
      <button {...props}>
        <div className="flex items-center">
          <span className={twMerge("mr-4", iconContainerClassName)}>
            <Icon {...props.IconProps} />
          </span>
          <div className="flex flex-col text-left ">
            <CustomText
              text={title}
              fontWeight="font-medium"
              fontSize="text-sm"
            />
            <CustomText
              text={description}
              fontWeight="font-normal"
              className="text-[11px]"
            />
          </div>
        </div>
        <div>
          <svg
            width="8"
            height="12"
            viewBox="0 0 8 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.90001 5.9998L1.00001 2.0998C0.816672 1.91647 0.725006 1.68314 0.725006 1.3998C0.725006 1.11647 0.816672 0.883138 1.00001 0.699804C1.18334 0.516471 1.41667 0.424805 1.70001 0.424805C1.98334 0.424805 2.21667 0.516471 2.40001 0.699804L7.00001 5.2998C7.10001 5.3998 7.17101 5.50814 7.21301 5.6248C7.25501 5.74147 7.27567 5.86647 7.27501 5.9998C7.27434 6.13314 7.25367 6.25814 7.21301 6.3748C7.17234 6.49147 7.10134 6.5998 7.00001 6.6998L2.40001 11.2998C2.21667 11.4831 1.98334 11.5748 1.70001 11.5748C1.41667 11.5748 1.18334 11.4831 1.00001 11.2998C0.816672 11.1165 0.725006 10.8831 0.725006 10.5998C0.725006 10.3165 0.816672 10.0831 1.00001 9.8998L4.90001 5.9998Z"
              fill={"currentColor"}
            />
          </svg>
        </div>
      </button>
    );
  }

  return (
    <button {...props}>
      <div className="flex justify-start items-center">
        <span className={twMerge("mr-5", iconContainerClassName)}>
          <Icon {...props.IconProps} />
        </span>
        {title}
      </div>
    </button>
  );
};

function DashboardActionButton({
  gradientInfo,
  title,
  Icon,
  ...rest
}: Readonly<DashboardActionButtonProps>): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);
  const { isTourActive } = useAdminContext();
  const isMobile = useMobile();
  const commonStyles = `${
    !isTourActive && "hover:border-[#18B5B7]"
  } flex justify-start items-center my-6 xl:m-6 p-4 font-[550] rounded-[32px] w-full md:min-w-[300px] lg:text-lg lg:min-w-[339px]`;
  const normalGradient = `linear-gradient(to right, ${gradientInfo?.startColor}, ${gradientInfo?.endColor})`;
  const hoverGradient = `linear-gradient(to right, ${gradientInfo?.hoverStart}, ${gradientInfo?.hoverMiddle}, ${gradientInfo?.hoverEnd})`;
  const GradientButtonClassNames = isMobile
    ? [
        "flex justify-between items-center w-full bg-primary text-white rounded-[4px] py-1.5 px-3",
        rest.className,
      ]
    : [
        `transition-all ${
          isTourActive && "cursor-not-allowed"
        } xs:w-[100%] md:w-[300px] lg:w-[360px] border shadow-[0_0_6px_rgba(100,113,136,0.36)] bg-gradient-to-r from-[${
          gradientInfo?.startColor
        }] to-[${gradientInfo?.endColor}] hover:from-[${
          gradientInfo?.hoverStart
        }] via-[${gradientInfo?.hoverMiddle}] to-[${
          gradientInfo?.hoverEnd
        }] text-white `,
        commonStyles,
        rest.className,
      ];
  const RegularButtonClassNames = isMobile
    ? [
        "flex justify-between items-center w-full bg-white text-[#001F53] rounded-[4px] p-3",
        rest.className,
      ]
    : [
        `box-border xs:w-[100%] md:w-[300px] lg:w-[360px] ${
          isTourActive
            ? "cursor-not-allowed"
            : "hover:border-[#18B5B7] hover:border-[1px] "
        } border shadow-[0_0_6px_rgba(100,113,136,0.36)] text-royalBlue  bg-white ${
          rest.className
        }`,
        commonStyles,
        rest.className,
      ];

  if (!!gradientInfo)
    return (
      <RequiredFieldTooltip {...rest.toolTipInfo}>
        <ActionButton
          {...rest}
          Icon={Icon}
          title={title}
          isMobile={isMobile}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={isTourActive}
          style={
            isMobile
              ? {}
              : {
                  background: isHovered ? hoverGradient : normalGradient,
                  transition: "all .3s ease",
                  opacity: rest?.disabled ? 0.5 : 1,
                }
          }
          className={twMerge(GradientButtonClassNames)}
        />
      </RequiredFieldTooltip>
    );
  return (
    <ActionButton
      {...rest}
      Icon={Icon}
      title={title}
      isMobile={isMobile}
      className={twMerge(RegularButtonClassNames)}
    />
  );
}
export default DashboardActionButton;
